/**
 * The brand palette.
 *
 * This file is a mirror of `tokens/colors.css` in the "Gynger Design System"
 * Claude Design project, which is the brand's own source. Step numbers and
 * values match it exactly so the two can be diffed by eye — if a value here
 * disagrees with the brandbook, this file is wrong, not the brandbook.
 *
 * The brand names each colour in French; the keys below are the English names
 * the codebase already uses, with the brand name recorded against each so the
 * mapping stays traceable:
 *
 *   teal   = paon        the seriousness. Replaces black for all text.
 *   mint   = turquoise   the medical. Scrubs, health.
 *   coral  = peche       the femininity. In touches, never as the stage.
 *   sand   = nude        the serenity. The ground under illustrations.
 *
 * The brand's rule is that no colour outside this palette is allowed. The
 * `derived` block at the bottom is the honest exception, and every entry in it
 * is a mix of two values that are already here — see the note there.
 *
 * This is the only file in the app permitted to contain a hex literal. Nothing
 * in `components/` should import it: reach for a semantic token instead, and if
 * none fits, add one to `semantic.ts`.
 */

/** Brand colours, at the exact values the brandbook specifies. */
export const brand = {
  /** paon — the primary. Replaces black for every piece of text. */
  paon: '#033744',
  /** turquoise — the medical accent. */
  turquoise: '#84D1BF',
  /** peche — the warm accent, used in touches. */
  peche: '#FF8B7C',
  /** nude — the calm ground. */
  nude: '#F2E5DD',
  white: '#FFFFFF',
} as const;

/**
 * The technical declinaisons.
 *
 * The design system is explicit that these are for interface work only —
 * states, borders, fills — and must never be used as a brand flat. The anchor
 * sits at its own step verbatim, so `palette.teal[700]` is exactly `#033744`.
 */
export const palette = {
  teal: {
    900: '#022A34',
    700: brand.paon,
    500: '#0A5364',
    300: '#4C7B87',
    100: '#D0DBDF',
  },
  mint: {
    600: '#5EB8A2',
    400: brand.turquoise,
    200: '#C0E8DE',
    50: '#EAF7F3',
  },
  coral: {
    600: '#F4705F',
    400: brand.peche,
    200: '#FFC3BA',
    50: '#FFEDEA',
  },
  sand: {
    300: '#E4D2C7',
    200: brand.nude,
    100: '#F9F2EE',
    50: '#fbf7f5',
  },

  white: brand.white,

  /**
   * Steps the app needs and the brand palette does not define.
   *
   * A deck has no disabled inputs, no placeholder text and no chat bubbles, so
   * the source system has no reason to carry them. Rather than introduce new
   * hues, each of these is a mix between two values already above, and each is
   * here for a stated reason:
   *
   * - `mintText` / `coralText` — the accents at a weight that can carry text.
   *   The design system's `--text-accent` is `peche` at full strength, which is
   *   2.27:1 on white and cannot be read; `--state-success` as text is 2.37:1.
   *   Those are fill colours doing a text job. These two are the same hues
   *   mixed toward the teal ink until they clear 4.5:1 against the *worst*
   *   light surface in the app (nude, not white) — 4.58:1 and 4.53:1, and
   *   5.65:1 / 5.58:1 on white.
   * - `tealMid` / `tealText` — supporting text and metadata, as two visibly
   *   different tiers that both clear AA on *every* light surface the app uses.
   *   The design system's `--text-secondary` (#4C7B87) is 4.68:1 on white, so
   *   it passes there and nowhere else: it measures 4.25:1 on the mint panel
   *   and 3.79:1 on nude, both of which the app puts text on. These are the
   *   same hue darkened until the worst of those surfaces clears 4.5:1.
   * - `neutral*` — cool greys mixed from white toward teal 100, for chat
   *   bubbles and hairlines where a warm sand tint reads as a colour rather
   *   than as a surface.
   * - `disabledText` is the design system's own `--state-disabled-fg`.
   */
  derived: {
    mintText: '#39706E',
    coralText: '#885D5A',
    tealMid: '#2B6776',
    tealText: '#396F7D',
    neutral50: '#F4F7F8',
    neutral100: '#EFF2F4',
    neutral200: '#E0E8EA',
    disabledText: '#A8968C',
  },
} as const;

/**
 * The symptom-severity scale: very good, good, average, bad, very bad.
 *
 * A second, explicit exception alongside `derived` — not a mix of brand
 * colours like that block, but a genuine traffic-light gradient (green
 * through yellow to red) specified outright for this one purpose, because
 * severity reads faster as a stoplight than as a ramp confined to the
 * brand's own teal/mint/peche/nude hues. It is not colour-blind-safe the way
 * the brand-derived ramp it replaced was (monotonic in lightness, so it held
 * up in greyscale and for red/green colour blindness); that trade-off was
 * made deliberately, in favour of the more legible metaphor, not overlooked.
 */
export const symptomSeverity = {
  veryGood: '#84D1A1',
  good: '#C0DA89',
  average: '#F8D752',
  bad: '#E3A266',
  veryBad: '#E47866',
} as const;

/**
 * The app's one deliberate yellow, a third explicit exception.
 *
 * The brand palette is teal/mint/peche/nude only and has no yellow, but the
 * licence-code tier badge reproduces its star colour from before the rebrand.
 * Kept here rather than in the component so the exception is visible in the
 * one file that is allowed hex literals.
 */
export const gold = '#EDC27E';

/** The brand names, for anywhere that needs to talk about the palette itself. */
export const brandColors = brand;

/**
 * Adds an alpha channel to a hex colour, returning 8-digit hex.
 *
 * 8-digit hex rather than `rgba()` because it is a plain string that survives
 * anywhere a colour is expected — react-jss, inline styles, canvas, and React
 * Native, which does not accept `rgba()` in every position.
 */
export function withAlpha(hex: string, alpha: number): string {
  const clamped = Math.max(0, Math.min(1, alpha));
  const channel = Math.round(clamped * 255)
    .toString(16)
    .padStart(2, '0')
    .toUpperCase();
  return `${hex}${channel}`;
}
